/**
 * Country choropleth layer - colors countries by regulation status.
 * Uses Mapbox's built-in country boundaries with data-driven styling.
 */
import type mapboxgl from 'mapbox-gl';
import type { CountryData } from '../types';
import { STATUS_CONFIG } from '../types';

/**
 * Build a Mapbox match expression that maps ISO2 codes to fill colors
 * based on regulation status.
 */
export function buildColorExpression(countries: CountryData[]): mapboxgl.Expression {
  const expr: any[] = ['match', ['get', 'iso_3166_1']];

  for (const country of countries) {
    if (country.iso2) {
      expr.push(country.iso2, STATUS_CONFIG[country.regulation_status].color);
    }
  }

  // Default fallback for countries not in our dataset
  expr.push('rgba(50, 50, 60, 0.3)');
  return expr as mapboxgl.Expression;
}

/**
 * Add the country fill layer to the map, colored by regulation status.
 * Uses Mapbox's built-in "country-boundaries" source which is available
 * in Mapbox GL JS v3+ with the default styles.
 */
export function addCountryLayer(map: mapboxgl.Map, countries: CountryData[]) {
  const colorExpr = buildColorExpression(countries);

  // Country fill layer using mapbox-country-boundaries tileset
  map.addSource('country-boundaries', {
    type: 'vector',
    url: 'mapbox://mapbox.country-boundaries-v1',
  });

  // Filled polygons for each country
  map.addLayer(
    {
      id: 'country-fills',
      type: 'fill',
      source: 'country-boundaries',
      'source-layer': 'country_boundaries',
      paint: {
        'fill-color': colorExpr,
        'fill-opacity': 0.65,  // fixed — no zoom variation keeps all colours consistent
      },
    },
    // Insert below labels
    'country-label'
  );

  // Country borders with subtle glow
  map.addLayer(
    {
      id: 'country-borders',
      type: 'line',
      source: 'country-boundaries',
      'source-layer': 'country_boundaries',
      paint: {
        'line-color': 'rgba(255, 255, 255, 0.15)',
        'line-width': [
          'interpolate', ['linear'], ['zoom'],
          1.5, 0.3,
          5, 0.8,
        ],
      },
    },
    'country-label'
  );

  // Highlighted border for hovered country
  map.addLayer(
    {
      id: 'country-borders-hover',
      type: 'line',
      source: 'country-boundaries',
      'source-layer': 'country_boundaries',
      paint: {
        'line-color': '#ffffff',   // overridden per-style in main.ts addLayers()
        'line-width': 2,
      },
      filter: ['==', 'iso_3166_1', ''],
    },
    'country-label'
  );

  // Transparent fill — always rendered so clicks/hovers work even when the
  // choropleth fills are hidden or the base style is light/satellite.
  map.addLayer(
    {
      id: 'country-hit-area',
      type: 'fill',
      source: 'country-boundaries',
      'source-layer': 'country_boundaries',
      paint: {
        'fill-color': 'rgba(0,0,0,0)',
        'fill-opacity': 0,
      },
    },
    'country-label'
  );
}

/** Rebuild and apply country fill colors (used by admin editor) */
export function updateCountryColors(map: mapboxgl.Map, countries: CountryData[]) {
  const colorExpr = buildColorExpression(countries);
  map.setPaintProperty('country-fills', 'fill-color', colorExpr);
}

/** Update the hover highlight to show a specific country */
export function setHoveredCountry(map: mapboxgl.Map, iso2: string | null) {
  map.setFilter('country-borders-hover', [
    '==',
    'iso_3166_1',
    iso2 || '',
  ]);
}
